import { Button, color } from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/InvestorDocUpload";
import { investorinddata } from "./Data";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import {
  initializeDocuments,
  initializeIndustryDetails,
  initializePersonalDetails,
  initializeAddressDetails,
  initializeBankDetails,
} from "../../Utils/dispatchIndividual";
import { useDisclosure } from "@mantine/hooks";
import { RegtankOnboardingModal } from "@funded-here-interface/common/src/components/Regtank/RegtankOnboardingModel";
import { useBuildRegtankOnboardModel } from "@funded-here-interface/common/src/hooks/useBuildRegtankOnboardModel";
import { useRegtankOnboardingClick } from "@funded-here-interface/common/src/hooks/useRegtankOnboardingClick";

// Custom hooks and utilities
import { useIndividualInvestorOrgSubmission } from "./hooks/useIndividualInvestorOrgSubmission";
import {
  extractFilePaths,
  getExistingFilesForIndividualInvestorType,
} from "./utils/documentHelpers";
import { useNavigateAfterRegtank } from "@funded-here-interface/common/src/hooks/useNavigateAfterRegtank";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { sortRegtankRequestsByExpiry } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

const IndInvestorDoc: FC = () => {
  const { classes } = useStyle();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Redux selectors - Auth
  const {
    userId,
    kycStatus,
    orgId,
    token,
    userFirstName,
    userLastName,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
    declarationAISubmitted,
  } = useSelector((state: RootState) => state.auth);

  // Redux selectors - Documents
  const {
    proof: proofOfAddress,
    salaryProof,
    bank: bankStatement,
  } = useSelector((state: RootState) => state.bordoc);

  // Redux selectors - Professional state
  const profState = useSelector((state: RootState) => state.prof);

  // Redux selectors - Bank state
  const bankState = useSelector((state: RootState) => state.bankInd);

  // Regtank onboarding
  const [openedRegtankOnboarding, modalActionRegtank] = useDisclosure(false);
  const pendingOnBoardingAs = "investor";
  const { handleNavigateNext } = useNavigateAfterRegtank();
  const modalContent = useBuildRegtankOnboardModel(pendingOnBoardingAs);
  const { isLoadingRegtank, setRegtankOnboardingUrl, handleRegtankClick } =
    useRegtankOnboardingClick(token);
  const hasFetched = useRef(false);

  // Custom hooks - single instantiation returns both functions
  const { submitIndividualInvestorOrganization, handleSubmissionSuccess } =
    useIndividualInvestorOrgSubmission({
      setIsLoading,
      onError: (error) => toast.error(error.message),
      setRegtankOnboardingUrl,
      handleNavigateNext,
    });
  const [latestKYCStatus, setLatestKYCStatus] = useState("");

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!hasFetched.current && !isLoading && userId) {
        hasFetched.current = true; // Set immediately to prevent duplicate calls
        setIsLoading(true);
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });
          //Get latest data of kyc
          if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
            setLatestKYCStatus(data.user.organization.kycStatus);
          }
          if (data && isMounted) {
            // Use existing initialize helper functions
            initializeDocuments(dispatch, data);
            initializeIndustryDetails(dispatch, data);
            initializePersonalDetails(dispatch, data);
            initializeAddressDetails(dispatch, data);
            initializeBankDetails(dispatch, data);
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
          hasFetched.current = false; // Reset on error to allow retry
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      latestKYCStatus === KYCStatus.PENDING_APPROVAL &&
      regtankOnboardingFeatureFlag &&
      regtankOnboarding &&
      regtankRequest &&
      regtankRequest.length > 0
    ) {
      const sortedRegtankRequest = sortRegtankRequestsByExpiry(regtankRequest);
      handleSubmissionSuccess({
        regtankOnboardingFeatureFlag,
        regtankOnboarding,
        regtankRequest: sortedRegtankRequest,
        declarationAISubmitted,
        openRegtankModal: modalActionRegtank.open,
        isPendingResubmission: false,
      });
    }
  }, [
    latestKYCStatus,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
    declarationAISubmitted,
    modalActionRegtank.open,
    handleSubmissionSuccess,
  ]);

  /**
   * Save draft to backend
   */
  const saveDraft = async (showToast: boolean = true) => {
    try {
      await saveOrgDraftMutation.mutateAsync({
        userId: parseInt(userId),
        token,
        proofOfAddress,
        salaryProof,
        bankStatement,
      });
      if (showToast) {
        toast.success("Draft Saved");
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = async () => {
    await saveDraft(false);
    navigate("/bank-details-ind");
  };

  /**
   * Handle next - validate documents and submit organization
   */
  const handleNext = async () => {
    await saveDraft(false);

    // Clear previous errors
    setErrors({});

    // Define validation rules for required documents
    const requiredDocuments = [
      {
        file: proofOfAddress,
        errorKey: "proofOfAddress",
        displayName: "Proof of Residential Address",
      },
      {
        file: salaryProof,
        errorKey: "salaryProof",
        displayName: "Financial Documents",
      },
      {
        file: bankStatement,
        errorKey: "bankStatement",
        displayName: "Proof of Bank Account Details",
      },
    ];

    // Validate all required documents
    const newErrors: { [key: string]: string } = {};
    const missingDocuments: string[] = [];

    requiredDocuments.forEach(({ file, errorKey, displayName }) => {
      if (!file || file.length === 0) {
        newErrors[errorKey] = `${displayName} is required`;
        missingDocuments.push(displayName);
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(`Please upload ${missingDocuments.join(", ")}`);
      return;
    }

    // Build documents upload object with file paths
    const documentsUpload = {
      proofOfAddress: extractFilePaths(proofOfAddress),
      salaryProof: extractFilePaths(salaryProof),
      bankStatement: extractFilePaths(bankStatement),
    };

    // Submit organization (create or update based on KYC status)
    submitIndividualInvestorOrganization({
      kycStatus: kycStatus || null,
      orgId: orgId?.toString() || null,
      token,
      userFirstName: userFirstName as string,
      userLastName: userLastName as string,
      profState,
      bankState,
      documentsUpload,
      regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag || false,
      regtankOnboarding: regtankOnboarding || false,
      regtankRequest,
      declarationAISubmitted: declarationAISubmitted || false,
      openRegtankModal: modalActionRegtank.open,
    });
  };

  return (
    <>
      <p className={classes.headinfo}>
        All uploads must be in PDF format. The file size should not exceed 10MB
        each.
        <br />
        We may request for additional supporting documents beyond those listed
        here as part of our onboarding and verification process.
      </p>
      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", background: "#fff" }}
      >
        {investorinddata.map((item) => (
          <FileUploadComponent
            key={item.number}
            number={item.number}
            fileId={item.id}
            FileName={item.heading}
            isIndividualInvestor={true}
            existingFiles={getExistingFilesForIndividualInvestorType(item.id, {
              proofOfAddress,
              salaryProof,
              bankStatement,
            })}
            errors={errors}
          />
        ))}

        <div className={classes.buttonDiv2}>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleBack}
              backgroundColor={color.WHITE}
              textColor={color.FHGREEN}
              children="Back"
              border="1px solid #338F86"
              width="210px"
            />
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              disabled={isLoading}
              onClick={handleNext}
              textColor={color.WHITE}
              backgroundColor={color.FHGREEN}
              children="Submit"
              width="210px"
            />
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={saveDraft}
              textColor={color.WHITE}
              backgroundColor={color.LOANTEXT}
              children="Save Draft"
              width="100%"
            />
          </div>
        </div>
      </div>
      <RegtankOnboardingModal
        opened={openedRegtankOnboarding}
        onClose={modalActionRegtank.close}
        title={modalContent.regtankModalTitle}
        firstLine={modalContent.regtankFirstLine}
        secondLine={modalContent.regtankSecLine}
        onButtonClick={handleRegtankClick}
        isLoadingOnboarding={isLoadingRegtank}
      />
    </>
  );
};

export default IndInvestorDoc;
